import type { SVGProps } from 'react';

type IconProps = SVGProps<SVGSVGElement> & { size?: number };

const baseSvg = (size: number, rest: SVGProps<SVGSVGElement>): SVGProps<SVGSVGElement> => ({
  width: size,
  height: size,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.75,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
  focusable: false,
  ...rest,
});

export function MenuIcon({ size = 20, ...rest }: IconProps): JSX.Element {
  return (
    <svg {...baseSvg(size, rest)}>
      <path d="M3 6h18M3 12h18M3 18h18" />
    </svg>
  );
}

export function DashboardIcon({ size = 20, ...rest }: IconProps): JSX.Element {
  return (
    <svg {...baseSvg(size, rest)}>
      <rect x={3} y={3} width={7} height={9} rx={1.5} />
      <rect x={14} y={3} width={7} height={5} rx={1.5} />
      <rect x={14} y={12} width={7} height={9} rx={1.5} />
      <rect x={3} y={16} width={7} height={5} rx={1.5} />
    </svg>
  );
}

export function CamerasIcon({ size = 20, ...rest }: IconProps): JSX.Element {
  return (
    <svg {...baseSvg(size, rest)}>
      <path d="M3 7a2 2 0 0 1 2-2h2l1.5-2h7L17 5h2a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z" />
      <circle cx={12} cy={13} r={3.5} />
    </svg>
  );
}

export function RecordingsIcon({ size = 20, ...rest }: IconProps): JSX.Element {
  return (
    <svg {...baseSvg(size, rest)}>
      <circle cx={12} cy={12} r={9} />
      <circle cx={12} cy={12} r={3.5} fill="currentColor" stroke="none" />
    </svg>
  );
}

/** Live broadcast / signal glyph for module tabs. */
export function LiveViewIcon({ size = 20, ...rest }: IconProps): JSX.Element {
  return (
    <svg {...baseSvg(size, rest)}>
      <circle cx={12} cy={12} r={2} fill="currentColor" stroke="none" />
      <path d="M8.5 8.5a5.5 5.5 0 0 0 0 7" />
      <path d="M5.5 5.5a9.5 9.5 0 0 0 0 13" />
      <path d="M15.5 8.5a5.5 5.5 0 0 1 0 7" />
      <path d="M18.5 5.5a9.5 9.5 0 0 1 0 13" />
    </svg>
  );
}

export function StorageIcon({ size = 20, ...rest }: IconProps): JSX.Element {
  return (
    <svg {...baseSvg(size, rest)}>
      <ellipse cx={12} cy={5.5} rx={8} ry={2.5} />
      <path d="M4 5.5v6c0 1.4 3.6 2.5 8 2.5s8-1.1 8-2.5v-6" />
      <path d="M4 11.5v6c0 1.4 3.6 2.5 8 2.5s8-1.1 8-2.5v-6" />
    </svg>
  );
}

export function SchedulesIcon({ size = 20, ...rest }: IconProps): JSX.Element {
  return (
    <svg {...baseSvg(size, rest)}>
      <rect x={3} y={5} width={18} height={16} rx={2} />
      <path d="M3 9h18M8 3v4M16 3v4" />
    </svg>
  );
}

export function UsersIcon({ size = 20, ...rest }: IconProps): JSX.Element {
  return (
    <svg {...baseSvg(size, rest)}>
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx={9} cy={7} r={4} />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13A4 4 0 0 1 16 11" />
    </svg>
  );
}

export function ProfileIcon({ size = 20, ...rest }: IconProps): JSX.Element {
  return (
    <svg {...baseSvg(size, rest)}>
      <circle cx={12} cy={8} r={4} />
      <path d="M4 21a8 8 0 0 1 16 0" />
    </svg>
  );
}

export function FullscreenIcon({ size = 18, ...rest }: IconProps): JSX.Element {
  return (
    <svg {...baseSvg(size, rest)}>
      <path d="M3 9V5a2 2 0 0 1 2-2h4M21 9V5a2 2 0 0 0-2-2h-4M3 15v4a2 2 0 0 0 2 2h4M21 15v4a2 2 0 0 1-2 2h-4" />
    </svg>
  );
}

export function FullscreenExitIcon({ size = 18, ...rest }: IconProps): JSX.Element {
  return (
    <svg {...baseSvg(size, rest)}>
      <path d="M9 3v4a2 2 0 0 1-2 2H3M15 3v4a2 2 0 0 0 2 2h4M9 21v-4a2 2 0 0 0-2-2H3M15 21v-4a2 2 0 0 1 2-2h4" />
    </svg>
  );
}

export function SettingsIcon({ size = 18, ...rest }: IconProps): JSX.Element {
  return (
    <svg {...baseSvg(size, rest)}>
      <circle cx={12} cy={12} r={3} />
      <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.9l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.9-.3 1.7 1.7 0 0 0-1 1.6V21a2 2 0 1 1-4 0v-.1A1.7 1.7 0 0 0 9 19.3a1.7 1.7 0 0 0-1.9.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1A1.7 1.7 0 0 0 4.7 15a1.7 1.7 0 0 0-1.6-1H3a2 2 0 1 1 0-4h.1A1.7 1.7 0 0 0 4.7 9a1.7 1.7 0 0 0-.3-1.9l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1A1.7 1.7 0 0 0 9 4.7a1.7 1.7 0 0 0 1-1.6V3a2 2 0 1 1 4 0v.1A1.7 1.7 0 0 0 15 4.7a1.7 1.7 0 0 0 1.9-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.9 1.7 1.7 0 0 0 1.6 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1Z" />
    </svg>
  );
}

export function Sitemap360Icon({ size = 20, ...rest }: IconProps): JSX.Element {
  return (
    <svg {...baseSvg(size, rest)}>
      <circle cx={12} cy={12} r={9} />
      <ellipse cx={12} cy={12} rx={9} ry={3.5} />
      <path d="M3 12c2.5-3 6-4.5 9-4.5s6.5 1.5 9 4.5" />
      <path d="M3 12c2.5 3 6 4.5 9 4.5s6.5-1.5 9-4.5" />
    </svg>
  );
}

export function LiveDetectionsIcon({ size = 20, ...rest }: IconProps): JSX.Element {
  return (
    <svg {...baseSvg(size, rest)}>
      <rect x={2.5} y={6.5} width={13} height={11} rx={1.5} />
      <path d="m15.5 10.5 5-2v7l-5-2Z" />
      <circle cx={8.5} cy={12} r={2.5} />
    </svg>
  );
}

export function DigitalPatrolIcon({ size = 20, ...rest }: IconProps): JSX.Element {
  return (
    <svg {...baseSvg(size, rest)}>
      <path d="M12 2 4 5v6c0 5 3.5 8.5 8 11 4.5-2.5 8-6 8-11V5l-8-3Z" />
      <path d="m8.5 12 2.5 2.5L15.5 10" />
    </svg>
  );
}

export function AiAnalysisIcon({ size = 20, ...rest }: IconProps): JSX.Element {
  return (
    <svg {...baseSvg(size, rest)}>
      <rect x={6} y={6} width={12} height={12} rx={2} />
      <path d="M9 9h6v6H9z" />
      <path d="M3 9h3M3 15h3M18 9h3M18 15h3M9 3v3M15 3v3M9 18v3M15 18v3" />
    </svg>
  );
}

export function FlashbackIcon({ size = 20, ...rest }: IconProps): JSX.Element {
  return (
    <svg {...baseSvg(size, rest)}>
      <path d="M3 12a9 9 0 1 0 3-6.7" />
      <path d="M3 4v4h4" />
      <path d="M12 7v5l3 2" />
    </svg>
  );
}

export function AiReportsIcon({ size = 20, ...rest }: IconProps): JSX.Element {
  return (
    <svg {...baseSvg(size, rest)}>
      <path d="M6 3h8l4 4v14H6z" />
      <path d="M14 3v4h4" />
      <path d="M9 17v-3M12 17v-5M15 17v-2" />
    </svg>
  );
}

export function AdministrationIcon({ size = 20, ...rest }: IconProps): JSX.Element {
  return (
    <svg {...baseSvg(size, rest)}>
      <circle cx={12} cy={8} r={3.5} />
      <path d="M5 21a7 7 0 0 1 14 0" />
      <path d="m16 5 1.5-1.5M17.5 8.5H20M5 5 6.5 6.5M2 8.5h2.5" />
    </svg>
  );
}

export function LogoutIcon({ size = 20, ...rest }: IconProps): JSX.Element {
  return (
    <svg {...baseSvg(size, rest)}>
      <path d="M10 4H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h4" />
      <path d="m16 8 4 4-4 4" />
      <path d="M10 12h10" />
    </svg>
  );
}

export function NvrLogoMark({ size = 28, ...rest }: IconProps): JSX.Element {
  /* Placeholder mark until the real `Header logo image.svg` is provided.
     A small "play inside shield" glyph keeps the slot visually populated. */
  return (
    <svg {...baseSvg(size, rest)} strokeWidth={1.5}>
      <path d="M12 2 4 5v7c0 5 3.5 8.5 8 10 4.5-1.5 8-5 8-10V5l-8-3Z" />
      <path d="m10 9 5 3-5 3Z" fill="currentColor" stroke="none" />
    </svg>
  );
}
